import { Text } from '@mantine/core';
import {
  CellSort,
  DataTable,
  EmptyRowsFallback,
  TableFilters,
} from '@shared/ui';
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { formatDateTime } from '@shared/helpers';
import { type SortQuery, PaginateType } from '@shared/types';
import { companyVacanciesFiltersConfig, initialValues } from './constants';
import type { CompanyVacanciesFiltersQuery } from './types';
import {
  type Vacancy as VacancyType,
  useGetVacancies,
} from '@entities/vacancy';
import { getCityNames } from '@entities/region';
import { VacancyStatusBadge } from '@widgets/vacancy/vacancyStatus';

type CompanyInfoVacanciesProps = {
  companyId: number;
};

export const CompanyInfoVacancies = ({
  companyId,
}: CompanyInfoVacanciesProps) => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState<CompanyVacanciesFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({});
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '15',
  });

  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(Number(searchParams.get('perPage')));

  const { data: vacancies, isLoading } = useGetVacancies({
    filter: {
      ...filter,
      'company.id': companyId.toString(),
    },
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      const newSort = { ...sort };
      delete newSort[field];
      setSort(newSort);
      return;
    }
    setSort({
      [field]: direction,
    });
  };

  const handleRowClick = ({ record }: { record: VacancyType }) => {
    navigate(`/vacancy-list/${record.id}`);
  };

  const emptyState = useMemo(() => {
    const isFilledFilters = Boolean(Object.keys(filter).length);

    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }

    return {
      desc: 'Список вакансий пуст',
      subDesc:
        'Чтобы создать вакансию в компании, нажмите кнопку «Создать вакансию»',
    };
  }, [filter]);

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => handleSortChange('id', dir)}
            currentDirection={sort.id}
          />
        ),
      },
      {
        accessor: 'name',
        title: (
          <CellSort
            label='Вакансия'
            onSort={dir => handleSortChange('name', dir)}
            currentDirection={sort.name}
          />
        ),
      },
      {
        accessor: 'createdAt',
        title: (
          <CellSort
            label='Дата создания'
            onSort={dir => handleSortChange('createdAt', dir)}
            currentDirection={sort.createdAt}
          />
        ),
        render: ({ createdAt }: VacancyType) => formatDateTime(createdAt),
      },
      {
        accessor: 'user.name',
        title: (
          <CellSort
            label='Рекрутер'
            onSort={dir => handleSortChange('responsibleUser', dir)}
            currentDirection={sort.responsibleUser}
          />
        ),
        render: (record: VacancyType) => (
          <Text>{`${record.responsibleUser.profile.firstName} ${record.responsibleUser.profile.lastName}`}</Text>
        ),
      },
      {
        accessor: 'cities',
        title: (
          <CellSort
            label='Город'
            onSort={dir => handleSortChange('cities.name', dir)}
            currentDirection={sort['cities.name']}
          />
        ),
        render: (record: VacancyType) => <Text>{getCityNames(record)}</Text>,
      },
      {
        accessor: 'salary',
        title: (
          <CellSort
            label='Уровень дохода'
            onSort={dir => handleSortChange('salary', dir)}
            currentDirection={sort.salary}
          />
        ),
        render: (record: VacancyType) => (
          <Text>{`${record.salaryFrom} - ${record.salaryTo}`}</Text>
        ),
      },
      {
        accessor: 'status',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => handleSortChange('status', dir)}
            currentDirection={sort.status}
          />
        ),
        render: (record: VacancyType) => (
          <VacancyStatusBadge status={record.status} />
        ),
      },
    ],
    [sort]
  );

  return (
    <>
      <TableFilters
        config={companyVacanciesFiltersConfig}
        initialValues={initialValues}
        setFilter={setFilter}
      />

      <DataTable<VacancyType>
        fetching={isLoading}
        columns={columns}
        records={vacancies?.data || []}
        onRowClick={handleRowClick}
        page={page}
        setPage={(page: number) => {
          setPage(page);
          setSearchParams({
            page: page.toString(),
            perPage: pageSize.toString(),
          });
        }}
        pageSize={pageSize}
        setPageSize={(size: number) => {
          setPageSize(size);
          setSearchParams({ page: '1', perPage: size.toString() });
        }}
        recordsPerPage={pageSize}
        onPageChange={setPage}
        totalRecords={vacancies?.pagination?.total || 0}
        emptyState={
          <EmptyRowsFallback
            desc={emptyState.desc}
            subDesc={emptyState.subDesc}
          />
        }
        highlightOnHover
      />
    </>
  );
};
